import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Col, Form, Select, Row, Space, Card, Spin } from 'antd';
import {
  roleApiEndpoints,
  customerApiEndpoints,
  companyAccessEndpoints,
  branchAccessEndpoints,
} from '@/api/endpoints';
import { handleUpdatePostApi } from '@/api/crud';
import { useFetch } from '@/hooks';
import { rules } from '@/utils/validation/FormValidation';
import { optionSearchFunction } from '@/utils/common';
import { STATUS } from '@/utils/statusCode';

const EditRoleMapping = () => {
  const [loading, setLoading] = useState(false);
  const [userData, setUserData] = useState([]);
  const [roles, setRoles] = useState([]);
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { id: userId } = useParams();
  const { data: companyData } = useFetch(branchAccessEndpoints.retrieveList);
  const abortController = new AbortController();

  useEffect(() => {
    (async function () {
      try {
        setLoading(true);
        const { signal } = abortController;
        const res = await roleApiEndpoints.retrieveUserRoleMapping(
          userId,
          signal,
        );
        if (res.data.ResponseCode === STATUS.successCode) {
          const { roleId, userId, branchId } = res.data.data;
          form.setFieldsValue({ roleId, userId, branchId });

          // Fetch users and roles for the initially selected company
          await fetchUsers(branchId);
        }
      } catch (error) {
        throw new Error(error);
      } finally {
        setLoading(false);
      }
    })();

    return () => {
      abortController.abort();
    };
  }, [userId]);

  const handleCompanyChange = (value) => {
    fetchUsers(value);
    form.setFieldsValue({
      roleId: [],
      userId: [],
    });
  };

  const fetchUsers = async (companyId) => {
    setLoading(true);
    try {
      const { data } =
        await customerApiEndpoints.companyWiseCustomer(companyId);
      setUserData(data.data);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section>
      <Card>
        <Form
          className="username"
          form={form}
          size="middle"
          layout="vertical"
          onFinish={(value) => {
            handleUpdatePostApi(
              userId,
              roleApiEndpoints.roleMappingUpdate,
              value,
              setLoading,
              form,
              () => {},
              () => navigate('/role/mapping/list/'),
            );
          }}
        >
          <Spin spinning={loading}>
            <Row gutter={[16, 16]}>
              <Col xs={{ span: 24 }} lg={{ span: 8 }}>
                <Form.Item
                  label="Select Branch"
                  name="branchId"
                  validateTrigger="onChange"
                  rules={rules.required}
                >
                  <Select
                    placeholder="Select Branch"
                    filterOption={(value, option) =>
                      optionSearchFunction(value, option)
                    }
                    options={companyData?.map((item) => ({
                      value: item.referenceId,
                      label: item?.name,
                    }))}
                    onChange={handleCompanyChange}
                  />
                </Form.Item>
              </Col>
              <Col xs={{ span: 24 }} lg={{ span: 8 }}>
                <Form.Item
                  label="Users"
                  name="userId"
                  rules={rules.required}
                  validateTrigger="onChange"
                >
                  <Select
                    placeholder="Users"
                    mode="multiple"
                    allowClear
                    options={userData.users?.map((item) => ({
                      value: item.referenceId,
                      label: item.name,
                    }))}
                  />
                </Form.Item>
              </Col>
              <Col xs={{ span: 24 }} lg={{ span: 8 }}>
                <Form.Item
                  label="Roles"
                  name="roleId"
                  rules={rules.required}
                  validateTrigger="onChange"
                >
                  <Select
                    placeholder="Role"
                    showSearch
                    filterOption={(value, option) =>
                      optionSearchFunction(value, option)
                    }
                    options={userData.roles?.map((item) => ({
                      value: item.referenceId,
                      label: item.name,
                    }))}
                  />
                </Form.Item>
              </Col>
            </Row>
            <div style={{ textAlign: 'right' }}>
              <Space size="large">
                <Button type="primary" htmlType="submit" disabled={loading}>
                  UPDATE
                </Button>
                <Button type="primary" danger onClick={() => navigate(-1)}>
                  CANCEL
                </Button>
              </Space>
            </div>
          </Spin>
        </Form>
      </Card>
    </section>
  );
};

export default EditRoleMapping;
